// src/components/ServicesGrid.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import { db } from "../lib/firebase";
import "./ServicesGrid.css";
import { CONFIG } from "../config";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";

/** ---- Config ---- */
const IMAGES_BASE = CONFIG.IMAGES_BASE;

const getImageUrl = (val) => {
  if (!val || typeof val !== "string") return "";
  if (/^https?:\/\//i.test(val) || /^\/\//.test(val)) return val;
  if (val.startsWith("/")) return val;
  return `${IMAGES_BASE}/${encodeURIComponent(val)}`;
};

export default function ServicesGrid({
  services = null,
  sectionTitle,
  sectionSubtitle,
  headerStyles = {},
  sectionStyles = {},
  fontStyles,
}) {
  const isMobile = useIsMobile();
  const [servicesState, setServicesState] = useState([]);
  const [titleState, setTitleState] = useState("");
  const [subtitleState, setSubtitleState] = useState("");

  const shouldFetch = services === null;

  // Fetch services list if not provided via props
  useEffect(() => {
    if (!shouldFetch) return;
    const servicesRef = ref(db, "homepageconfig/services");
    const unsub = onValue(servicesRef, (snap) => {
      const raw = snap.val();
      const list = Array.isArray(raw) ? raw : [];

      const normalized = list
        .map((it) => ({
          image: getImageUrl(it?.image || it?.img || ""),
          title: (it?.title || "").trim(),
          description: (it?.description || it?.desc || "").trim(),
          link: (it?.link || "").trim(),
        }))
        .filter((x) => x.title || x.image);

      setServicesState(normalized);
    });

    return () => unsub();
  }, [shouldFetch]);

  // Fetch section header if not provided via props
  useEffect(() => {
    if (!shouldFetch || sectionTitle || sectionSubtitle) return;
    const headRef = ref(db, "homepageconfig/servicesSection");
    const unsub = onValue(headRef, (snap) => {
      const v = snap.val() || {};
      setTitleState((v.title || "").trim());
      setSubtitleState((v.subtitle || "").trim());
    });

    return () => unsub();
  }, [shouldFetch, sectionTitle, sectionSubtitle]);

  const items = useMemo(() => {
    if (Array.isArray(services)) return services;
    return servicesState;
  }, [services, servicesState]);

  const title = sectionTitle || titleState;
  const subtitle = sectionSubtitle || subtitleState;

  if (!items.length) return null;

  const sectionTitleFont = fontStyles?.sectionTitle || fontStyles?.heading || {};
  const sectionDescFont = fontStyles?.sectionDescription || fontStyles?.subHeading || {};
  const headerSizeOverrides = headerStyles?.fontSizes || {};
  const sectionSizeOverrides = sectionStyles?.fontSizes || {};

  const titleStyle = {
    fontFamily: sectionTitleFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      headerSizeOverrides.sectionTitle ||
        headerSizeOverrides.heading ||
        sectionTitleFont?.fontSize ||
        undefined,
      isMobile
    ),
    fontWeight: sectionTitleFont?.fontWeight || 700,
    lineHeight: sectionTitleFont?.lineHeight || "1.2",
    color: headerStyles?.headingColor || sectionTitleFont?.color || "#111111",
  };

  const subtitleStyle = {
    fontFamily: sectionDescFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      headerSizeOverrides.sectionDescription ||
        headerSizeOverrides.subHeading ||
        sectionDescFont?.fontSize ||
        undefined,
      isMobile
    ),
    fontWeight: sectionDescFont?.fontWeight || 400,
    lineHeight: sectionDescFont?.lineHeight || "1.6",
    color: headerStyles?.subHeadingColor || sectionDescFont?.color || "#333333",
  };

  const cardTitleStyle = {
    fontFamily: fontStyles?.subHeading?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sectionSizeOverrides.subHeading ||
        sectionSizeOverrides.serviceTitle ||
        fontStyles?.subHeading?.fontSize ||
        undefined,
      isMobile
    ),
    fontWeight: fontStyles?.subHeading?.fontWeight || 600,
    lineHeight: fontStyles?.subHeading?.lineHeight || "1.3",
    color:
      sectionStyles?.serviceTitleColor ||
      sectionStyles?.headingColor ||
      fontStyles?.subHeading?.color ||
      "#111111",
  };

  const cardDescStyle = {
    fontFamily: fontStyles?.content?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sectionSizeOverrides.content ||
        sectionSizeOverrides.serviceContent ||
        fontStyles?.content?.fontSize ||
        undefined,
      isMobile
    ),
    fontWeight: fontStyles?.content?.fontWeight || 400,
    lineHeight: fontStyles?.content?.lineHeight || "1.6",
    color:
      sectionStyles?.serviceContentColor ||
      sectionStyles?.contentColor ||
      fontStyles?.content?.color ||
      "#666666",
  };

  return (
    <section className="svc-section">
      <div className="svc-wrap">
        {(title || subtitle) && (
          <header className="svc-head">
            {title && <h2 className="svc-title" style={titleStyle}>{title}</h2>}
            {subtitle && <p className="svc-subtitle" style={subtitleStyle}>{subtitle}</p>}
          </header>
        )}

        <div className="svc-grid" role="list">
          {items.map((c, idx) => {
            const Inner = (
              <>
                <div className="svc-card-inner">
                  <div className="svc-icon">
                    {c.image ? (
                      <img src={c.image} alt={c.title || "Service"} loading="lazy" />
                    ) : null}
                  </div>

                  {c.title ? (
                    <div className="svc-card-title" title={c.title} style={cardTitleStyle}>
                      {c.title}
                    </div>
                  ) : null}

                  {/* Keep description in DOM for SEO, hidden by CSS like screenshot */}
                  {c.description ? <p className="svc-desc" style={cardDescStyle}>{c.description}</p> : null}
                </div>
              </>
            );

            return c.link ? (
              <Link key={idx} to={c.link} className="svc-card" role="listitem" aria-label={c.title}>
                {Inner}
              </Link>
            ) : (
              <div key={idx} className="svc-card" role="listitem" tabIndex={0}>
                {Inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
